import type { Settings } from '@jt/shared';
import { useState } from 'react';
import { ApiError, post } from '../api/client';
import { useFiles } from '../api/hooks';
import { AreaField } from '../components/fields';
import { ConfirmButton, useUI } from '../components/overlays';

const PLACEHOLDER = `# Имя Фамилия
Senior QA Engineer · Город, страна · email · телефон · LinkedIn

## Опыт
### Senior QA Engineer — Компания, 2021 – сейчас
- Что делали и какой результат (цифры, если есть)

## Навыки
## Образование
## Языки`;

/** Мастер-резюме: единственный источник фактов для «Подготовить отклик». */
export function MasterCvBox({ s, set }: { s: Settings; set: (f: { masterCv: string }) => Promise<unknown> }) {
  const ui = useUI();
  const files = useFiles('resume');
  // резюме, подготовленные под вакансию, — не источник для мастер-резюме
  const stored = (files.data ?? []).filter(
    (f) => f.stored && /pdf|word|text/.test(f.mime) && !f.note.startsWith('Под вакансию:'),
  );
  const [fileId, setFileId] = useState('');
  const [busy, setBusy] = useState(false);
  const chosen = fileId || stored[0]?.id || '';

  const fill = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const r = await post<{ text: string }>('/settings/master-cv/from-file', { fileId: chosen });
      await set({ masterCv: r.text.slice(0, 20000) });
      ui.toast('Текст из файла вставлен — проверьте и поправьте');
    } catch (e) {
      ui.toast(e instanceof ApiError ? e.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="box" id="master-cv">
      <h2>Мастер-резюме</h2>
      <p className="muted sm">
        Полное резюме со всем опытом, цифрами, навыками и языками. «Подготовить отклик» берёт факты только
        отсюда: переставляет акценты под вакансию, но ничего не выдумывает. В Claude текст уходит только по
        этой кнопке; файлы резюме не отправляются.
      </p>
      <div className="grid">
        <AreaField
          label={`Текст резюме (Markdown или обычный текст) · ${s.masterCv.length} из 20 000`}
          name="masterCv"
          rows={14}
          value={s.masterCv}
          placeholder={PLACEHOLDER}
          onSave={(v) => set({ masterCv: v })}
        />
      </div>
      {stored.length > 0 && (
        <div className="btnrow" style={{ marginTop: 10 }}>
          <label className="f" style={{ minWidth: 220 }}>
            Заполнить из файла резюме
            <select value={chosen} onChange={(e) => setFileId(e.target.value)}>
              {stored.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.lang ? ` · ${f.lang}` : ''}
                </option>
              ))}
            </select>
          </label>
          {s.masterCv.trim() ? (
            <ConfirmButton
              className="btn"
              confirmText="Заменить текущий текст?"
              onConfirm={() => void fill()}
            >
              {busy ? 'Читаю файл…' : 'Заполнить из файла'}
            </ConfirmButton>
          ) : (
            <button className="btn" onClick={() => void fill()} disabled={busy}>
              {busy ? 'Читаю файл…' : 'Заполнить из файла'}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
